import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { CreateLeague, LeagueStatus, RenameLeague } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useLeagues(clubId: string | null, status?: LeagueStatus) {
	return useQuery({
		enabled: !!clubId,
		placeholderData: (prev) => prev,
		queryFn: () => clubApi.leagues.list(clubId as string, status),
		queryKey: queryKeys.leagues.list(clubId ?? "", status),
	});
}

export function useLeagueDetail(id: string | null) {
	return useQuery({
		enabled: !!id,
		queryFn: () => clubApi.leagues.get(id as string),
		queryKey: queryKeys.leagues.detail(id ?? ""),
	});
}

export function useCreateLeague(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateLeague) => clubApi.leagues.create(clubId, data),
		onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.leagues.all }),
	});
}

export function useRenameLeague() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: RenameLeague }) =>
			clubApi.leagues.rename(id, data),
		onSuccess: (_, { id }) => {
			qc.invalidateQueries({ queryKey: queryKeys.leagues.all });
			qc.invalidateQueries({ queryKey: queryKeys.leagues.detail(id) });
		},
	});
}

export function useArchiveLeague() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => clubApi.leagues.archive(id),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: queryKeys.leagues.all });
			qc.invalidateQueries({ queryKey: queryKeys.leagues.detail(id) });
		},
	});
}

export function useLeagueEntries(leagueId: string | null, seasonId?: string) {
	return useQuery({
		enabled: !!leagueId,
		queryFn: () => clubApi.leagues.entries.list(leagueId as string, seasonId),
		queryKey: queryKeys.leagues.entries(leagueId ?? "", seasonId),
	});
}

export function useRemoveLeagueEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ leagueId, userId }: { leagueId: string; userId: string }) =>
			clubApi.leagues.entries.remove(leagueId, userId),
		// The member count on the league card and header changes too.
		onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.leagues.all }),
	});
}
